/*
This component will be as a Parent componet for Product Details Page
*/

import React, { useEffect, useState, useRef, useMemo, useReducer } from "react";
import { useSelector } from "react-redux";
import Modal from "react-modal";

import styles from "../ProductDetails.module.scss";
import ProductImage from "../../ProductImage";
import ProductColorPicker from "../ColorPicker";
import GiftAddToBag from "../../common/GiftAddToBag";
import GiftProductInfo from "../GiftProductInfo";
import LetterPicker from "../LetterPicker";
import GiftSections from "../GiftSections";
import SlickSlider from "../../SlickCarousal/index";
import MobileCarousel from "../../MobileCarousel";
import GiftSizeDropup from "@/common/GiftSizeDropup";
import { viewProduct } from "@/components/common/Nosto";
import Seo from "../Seo";

import useMediaQuery from "@/helpers/hooks/useMediaQuery";
import {
  mapVariants,
  selectVariant,
  getProductOptions,
  getHexCodeData,
} from "@/helpers/pdpHelpers/pdpVariantHelpers";
import {
  formatPrice,
  getVariantPrice,
} from "@/helpers/pdpHelpers/priceHelpers";
import { groupImages } from "@/helpers/pdpHelpers/pdpVariantHelpers";
import { generateBreadcrumbsList } from "@/helpers/pdpHelpers/breadcrumbHelpers";
import { getTargetingData } from "@/helpers/pdpHelpers/cmsHelpers";

import GiftSizeDropdown from "../../common/GiftSizeDropdown/index";
import Breadcrumbs from "@/common/Breadcrumbs";
import ProductCarouselSection from "@/components/BuilderIO/ProductCarousel/ProductCarouselSection";
import { bodyOverflow } from "@/helpers/userHelpers";
import { getShopifyTagData } from "@/helpers/pdpHelpers";
import { contentViewed } from "../../common/Tracking";
import { attentiveIdentifyCustomer } from "@/components/common/Attentive";
import { useRouter } from "next/compat/router";
import Error from "../../../pages/404";
import StickyBottomOverlay from "@/components/common/StickyBottomOverlay";
import { useIntersection } from "@/components/common/UseIntersection";
import { BuilderComponent } from "@builder.io/react";

export default function GiftCardTemplate(productDetails) {
  const router = useRouter();
  const variantParam=router.query.variant ? true:false;
  const isMobile = useMediaQuery("(max-width: 767px)");
  const isPWA = useSelector((state) => state.pwa.is_PWA);

  const productDesc = JSON.stringify(productDetails.productDetails);
  const [breadcrumbList, setBreadcrumbList] = useState([]);
  const generateBreadcrumb = async (productTags) => {
    let ret = await generateBreadcrumbsList(productTags);
    setBreadcrumbList(ret);
  };
  useEffect(() => {
    generateBreadcrumb(productDetails.productDetails.tags);
  }, [productDetails.productDetails.tags]);
  const [open, setOpen] = useState(false);
  /* Start - Size Dropdown state and functions */
  const [isSizeDropdownOpen, setIsSizeDropdownOpen] = useState(false);
  /* End - Size Dropdown state and functions */

  const tagData = useMemo(
    () => getShopifyTagData(productDetails.productDetails.tags),
    [productDetails.productDetails.tags]
  );

  const productType = productDetails.productDetails?.product_type?.value
    ? productDetails.productDetails.product_type.value
    : "regular";

  const addToBagRef = useRef(null);
  let logUser = useSelector((state) => state.loginUser);
  const userEmail = logUser?.userData?.email;

  /* START - Main Variants Code */
  let { colors, sizes, letters } = useMemo(
    () => getProductOptions(productDetails.productDetails.options),
    [productDetails]
  );
  if (letters) letters = letters.sort();

  const variants = productDetails.productDetails.variants.edges;
  let firstVariant = productDetails.productDetails.variants.edges[0].node;
  Object.values(variants).map((vari) => {
    if(vari?.node){
      const variantPrice = getVariantPrice(vari.node);
      let priceToCheck = variantPrice?.returnedSalePrice ? variantPrice.returnedSalePrice : variantPrice.returnedPrice;
      if (priceToCheck !== "$0") {
        firstVariant = vari.node;
        return;
      }
    }
  });
  

  const variantMap = useMemo(
    () =>
      mapVariants(variants, colors !== null, sizes !== null, letters !== null),
    [variants, colors, sizes, letters]
  );

  if(variantMap){
    if(letters && letters.length > 0){
      letters = hideZeroPriceColor(letters, variantMap);
    }
    if(colors && colors.length > 0){
      colors = hideZeroPriceColor(colors, variantMap);
    }
  }

  const [selectedColor, setSelectedColor] = useState(colors ? colors.find(el => el !== undefined) : null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedLetter, setSelectedLetter] = useState(
    letters ? letters.find(el => el !== undefined) : null
  );
  const hexCodeArray = useMemo(() => {
    if (colors) {
      return getHexCodeData(variantMap);
    } else {
      return null;
    }
  }, [variantMap, colors]);
  
  useEffect(() => {
    setSelectedColor(colors ? colors.find(el => el !== undefined) : null);
    setSelectedSize(null);
    setSelectedLetter(letters ? letters.find(el => el !== undefined) : null);
  }, [colors, sizes, letters]);

  useEffect(() => {
  if(variantParam){
      const variantIds = productDetails.productDetails.variants.edges.map((element => {
      var variantIdConvert = Buffer(element.node.id, "base64").toString('ascii');
      return variantIdConvert.substring(variantIdConvert.lastIndexOf("/") + 1);
    }));
    const variantUrlId = router.query.variant;
    if (variantIds.includes(variantUrlId)) {
      const indexOfVariant = variantIds.indexOf(variantUrlId)
      const variant = productDetails.productDetails.variants.edges[indexOfVariant].node;
      const color = variant.selectedOptions.find((option) => option.name.toLowerCase() === "color")?.value;
      const size = variant.selectedOptions.find((option) => option.name.toLowerCase() === "size" || option.name.toLowerCase() === "denominations")?.value;
      const letter = variant.selectedOptions.find((option) => option.name.toLowerCase() === "style")?.value;
      if(color) setSelectedColor(color);
      if(size) setSelectedSize(size);
      if(letter) setSelectedLetter(letter);
    }
  }
}, [router.query.variant]);

  // To change the selected variant if a color or size or letter is selected by the user
  const selectedVariant = selectVariant(
    firstVariant,
    variantMap,
    selectedColor,
    selectedSize,
    selectedLetter,
    sizes ? sizes.length : 0,
    setSelectedSize
  );

  const imageGroups = useMemo(() => {
    return groupImages({
      mediaEdges: productDetails.productDetails.media.edges,
    });
  }, [productDetails.productDetails.media.edges]);

  const productImage = useMemo(() => {
    // Check if product has images (imageGroups != empty object)
    if (imageGroups && Object.keys(imageGroups).length > 0) {
      if (imageGroups[selectedVariant.image.altText]) {
        return imageGroups[selectedVariant.image.altText];
      } else {
        // Default first image group
        return imageGroups[Object.keys(imageGroups)[0]];
      }
    } else {
      return [];
    }
  }, [imageGroups, selectedVariant?.image?.altText]);

  const [isOpen, setIsOpen] = useState(false);
  function toggleModal() {
    setIsOpen(!isOpen);
  }

  useEffect(()=>{
    if (isOpen) {
      bodyOverflow();
    } else {
      bodyOverflow(false);
    }
  },[isOpen])
  
  // To pass to Sizes component as prop
  // sizeVariants = sizes corresponding to currently selected color/letter if colors/letters were retrieved from shopify or just the sizes if only sizes were retrieved
  let sizeVariants = null;
  if (variantMap) {
    if (colors && sizes) {
      sizeVariants = variantMap[selectedColor];
    } else if (letters && sizes) {
      sizeVariants = variantMap[selectedLetter];
    } else if (sizes) {
      sizeVariants = variantMap;
    }
  }
  
  // To set price on change of the selected variant
  let price,
    salePrice,
    priceNoFormat,
    salePriceNoFormat = null;
  let variantOlivelaCost = 0;
  // Set price of first variant as default
  if (firstVariant) {
    price = formatPrice(firstVariant.priceV2.amount);
  }

  let priceObj,
    priceNoFormatObj = { returnedPrice: null, returnedSalePrice: null };
  if (selectedVariant) {
    priceObj = getVariantPrice(selectedVariant, productType);
    priceNoFormatObj = getVariantPrice(selectedVariant, productType, false);
    if (typeof selectedVariant?.olivela_cost?.value != "undefined")
      variantOlivelaCost = selectedVariant?.olivela_cost?.value;
  }

  if (priceObj.returnedPrice) {
    price = priceObj.returnedPrice;
    priceNoFormat = priceNoFormatObj.returnedPrice - variantOlivelaCost - 9;
  }
  if (priceObj.returnedSalePrice) {
    salePrice = priceObj.returnedSalePrice;
    salePriceNoFormat =
      priceNoFormatObj.returnedSalePrice - variantOlivelaCost - 9;
  }

  let finalProductPrice = salePrice ? salePrice : price;
  if (finalProductPrice == "$0") {
    Object.values(variants).map((vari, index) => {
      let priceToCheck = getVariantPrice(vari.node)?.returnedSalePrice ? getVariantPrice(vari.node).returnedSalePrice : getVariantPrice(vari.node).returnedPrice;
      if (priceToCheck !== "$0") {
        finalProductPrice = priceToCheck;
        return;
      }
    });
  }

  // OS-854 add alt tags and title to images
  const media = JSON.parse(JSON.stringify(productImage));
  if (media) {
    media.forEach((item, index, array) => {
      array[
        index
      ].alt = `${productDetails.productDetails.vendor} ${productDetails.productDetails.title} ${index}`;
      array[index].title = `${productDetails.productDetails.title} ${index}`;
    });
  }

  function add3Dots(string, limit) {
    var dots = "...";
    string = string.replace(/(<([^>]+)>)/gi, "");
    if (string.length > limit) {
      // you can also use substr instead of substring
      string = string.substring(0, limit) + dots;
    }
    
    return string;
  }

  

  const builderTargetingData = useMemo(
    () =>
      getTargetingData({
        vendor: productDetails.productDetails.vendor,
        tags: productDetails.productDetails.tags,
        productId: productDetails.productDetails.id,
        productHandle: productDetails.productDetails.handle,
        collections: productDetails.productDetails.collections.edges,
      }),
    [productDetails]
  );

  useEffect(() => {
     let gaPrice = finalProductPrice
    gaPrice = finalProductPrice.replace(/\,/g,'');
    productDetails.productDetails.price = parseFloat(gaPrice.substring(1));
    productDetails?.productDetails?.tags?.map((element) => {
      if (element.split(":").shift() === "categories.lvl0") {
        productDetails.productDetails.named_tags = {
          category: element?.split(":").pop(),
        };
      }
    });
    viewProduct({
      productId: productDetails?.productDetails?.id,
    });
    dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    dataLayer.push({
      event: "pdp_page_view",
      PDP_Id: productDetails?.productDetails?.id.replace("gid://shopify/Product/", ""),
      ecommerce: {
        detail: {
          products: [
            {
              name: productDetails?.productDetails?.title, // Name or ID is required.
              id:productDetails?.productDetails?.id.replace("gid://shopify/Product/", ""),
              price: productDetails?.productDetails?.price,
              brand: productDetails?.productDetails?.vendor,
              category: productDetails?.productDetails?.productType,  //Category should be same as defined in productType
              variant: variants[variants.length - 1]?.node?.title,
            },
          ],
        },
      },
    });
    contentViewed(productDetails);
    localStorage.setItem(
      "pdp_page_view",
      JSON.stringify(productDetails.productDetails)
    );
  }, []);

  const nostoPlacements = useSelector((state) => state.nosto.nostoPlacements);

  let flag = 0;

  if(variantMap){
    Object.values(variantMap).map((sizeVariants) => {
      if (sizeVariants) {
        Object.values(sizeVariants).map((vari) => {
          const variantPrice = getVariantPrice(vari);
          let priceToCheck = variantPrice?.returnedSalePrice ? variantPrice.returnedSalePrice : variantPrice.returnedPrice;
          if (priceToCheck !== "$0") {
            flag = 1;
            return;
          }
        });
      } else {
        flag = 1;
      }
    });
  }
  else{
    flag = 1;
  }
  function hideZeroPriceColor(colors, variantMap){
    Object.values(colors).map((eachColor, index) => {
      let nonZeroVariant = false;
      if(typeof variantMap[eachColor] != "undefined" && variantMap[eachColor]){
        Object.values(variantMap[eachColor]).map((vari) => {
          const variantPrice = getVariantPrice(vari);
          let priceToCheck = variantPrice?.returnedSalePrice ? variantPrice.returnedSalePrice : variantPrice.returnedPrice;
          if (priceToCheck !== "$0") {
            nonZeroVariant = true;
            return;
          }
        });
      }
      if(!nonZeroVariant){
        delete colors[index];
      }
    });
    return colors;
  }

  const addToBagButtonRef = useRef(null);
  // State to manage loading state of the Add to Bag button
  const [addToBagLoading, setAddToBagLoading] = useState(false);

  // generate buttons to be shown on sticky bottom overlay
  const generateStickyButtons = () => {
    const buttons = [
      { label: addToBagLoading ? "" : "Add to bag", className: `ga-pdp-add-to-cart-overlay ${addToBagLoading ? "loading" : ""}`, onClick : () => addToBagButtonRef?.current?.click() },
    ];
    return buttons;
  }

  // Lazy load PDP Impact Content when visible using useIntersection
  const impactContentRef = useRef(null);
  // Set initialState to false for lazy loading, rootMargin to '200px' for preloading before fully in view
  const impactContentVisible = useIntersection(impactContentRef, "200px", false);

  return (
    <>
    { flag?(
      <><Seo
          productDetails={productDetails.productDetails}
          colors={colors}
          tagData={tagData}
          productImageGroups={imageGroups} /><Breadcrumbs breadcrumbList={breadcrumbList} /><div className="container bg-oat mw-1440">
            <Modal
              isOpen={isOpen}
              contentLabel="My dialog"
              style={{
                content: {
                  inset: 0,
                  padding: 0
                }
              }}
            >
              {/* <div><CustomCarousal data={data.slides} leadingText={data.leadingText} /></div> */}

              <SlickSlider productImage={media} onRequestClose={toggleModal} />
              {/* <button  className="text-end border-0">Close modal</button> */}
            </Modal>
            <div className="row">
              <div className="grid-md-7 grid-sm-6">
                <div className="row d-none d-md-block">
                  <div className="grid-md-11 grid-sm-12">
                    {media && (
                      <ProductImage
                        productImage={media}
                        device={"desktop"}
                        toggleModal={toggleModal} />
                    )}
                  </div>
                </div>
              </div>

              <div
                className="grid-xs-12 d-none d-sm-block mobile-pdp-main-slider"
                id="MobileProductImage"
              >
                <MobileCarousel
                  productImage={media}
                  device={"mobile"}
                  toggleModal={toggleModal} />
              </div>
              <div className="grid-md-5 grid-sm-6 grid-xs-12 mg-btm-20">
                <div className={`${styles.make_me_sticky} ${isPWA ? "reduce_zindex_98":""}`}>
                  <div className={styles.wrapper}>
                    <div className={styles.details_wrapper}>
                      <div ref={addToBagRef}>
                        <div className={`row ${styles.info_wrapper}`}>
                          <GiftProductInfo
                            productInfo={productDetails.productDetails}
                            />
                        </div>
                        <div className="row">
                        <p
                          id="descPDP"
                          className={`x-small-xs small-sm normal-md ${styles.note_wrapper_giftCard}`}
                        >
                          {add3Dots(
                            productDetails?.productDetails?.descriptionHtml
                              .split("<br>")[0],
                              // .replace("<p>", "")
                              // .replace("</p>","")
                              // .replace("<span>", "")
                              // .replace("</span>",""),
                            200
                          )}
                        </p>
                      </div>

                        

                        {productDetails.productDetails.tags.includes(
                          "personalized"
                        ) ? (
                          <LetterPicker
                            swatchesData={letters}
                            selectedLetter={selectedLetter}
                            setSelectedLetter={setSelectedLetter} />
                        ) : (
                          colors &&
                          colors[0] !== "No Color" && (
                            <div className={styles.color_wrapper}>
                              <ProductColorPicker
                                swatchesData={colors}
                                selectedColor={selectedColor}
                                setSelectedColor={setSelectedColor}
                                hexCodeArray={hexCodeArray} />
                            </div>
                          )
                        )}
                        {/* <div className={styles.letter_wrapper}></div> */}
                        <div
                          className={`${styles.size_wrapper} ${styles.full_width}`}
                        >
                          {productDetails.productDetails.availableForSale &&
                            sizeVariants && (
                              <>
                                {isMobile ? (
                                  <GiftSizeDropup
                                    id="pdpSizeUp"
                                    sizeVariants={sizeVariants}
                                    selectedSize={selectedSize}
                                    setSelectedSize={setSelectedSize}
                                    isSizeDropdownOpen={isSizeDropdownOpen}
                                    setIsSizeDropdownOpen={setIsSizeDropdownOpen}
                                    AddToBag={<GiftAddToBag
                                      id="sizeUpCTA"
                                      product={productDetails.productDetails}
                                      isSizeDropdownOpen={isSizeDropdownOpen}
                                      setIsSizeDropdownOpen={setIsSizeDropdownOpen}
                                      finalProductPrice={finalProductPrice}
                                      addToBagRef={addToBagRef}
                                      selectedVariant={selectedVariant}
                                      isSizeSelected={!sizeVariants ||
                                        Object.keys(sizeVariants).length === 1 ||
                                        selectedSize !== null}
                                      // if no selectedSize, set buttonText to null for default AddToBag component buttonText
                                      buttonText={selectedSize ? null : "Choose gift amount:"}
                                      setSelectedColor={setSelectedColor}
                                      setSelectedLetter={setSelectedLetter}
                                      setSelectedSize={setSelectedSize} />} />
                                ) : (
                                  <GiftSizeDropdown
                                    id="pdpSize"
                                    sizeVariants={sizeVariants}
                                    selectedSize={selectedSize}
                                    setSelectedSize={setSelectedSize}
                                    isSizeDropdownOpen={isSizeDropdownOpen}
                                    setIsSizeDropdownOpen={setIsSizeDropdownOpen}
                                     />
                                )}
                              </>
                            )}
                        </div>
                        <StickyBottomOverlay buttons={generateStickyButtons()} targetElementId="PDP-buttons"/>
                        <div id="PDP-buttons" className={`row ${styles.full_width}`}>
                          <div className={`${styles.cta_wrapper} ${styles.giftcard_cta}`}>
                            <GiftAddToBag
                              id="ctaMain"
                              product={productDetails.productDetails}
                              isSizeDropdownOpen={isSizeDropdownOpen}
                              setIsSizeDropdownOpen={setIsSizeDropdownOpen}
                              finalProductPrice={finalProductPrice}
                              addToBagRef={addToBagRef}
                              selectedVariant={selectedVariant}
                              isSizeSelected={!sizeVariants ||
                                Object.keys(sizeVariants).length === 1 ||
                                selectedSize !== null}
                              setSelectedColor={setSelectedColor}
                              setSelectedLetter={setSelectedLetter}
                              setSelectedSize={setSelectedSize} 
                              addToBagButtonRef={addToBagButtonRef}
                              handleAddToBagLoading={(e)=>setAddToBagLoading(e)}
                              />
                          </div>
                        </div>
                      </div>

                      <div className="d-block dcd">
                        <div className={styles.section_wrapper_giftCard}>
                          <GiftSections
                            productDesc={productDesc}
                             />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div><div
            className="nosto_element"
            id="productpage-nosto-2"
            dangerouslySetInnerHTML={{
              __html: nostoPlacements["productpage-nosto-2"]
                ? nostoPlacements["productpage-nosto-2"].html || nostoPlacements["productpage-nosto-2"]
                : ""
            }}
          ></div>
          <div
            className="nosto_element"
            id="productpage-nosto-3"
            dangerouslySetInnerHTML={{
              __html: nostoPlacements["productpage-nosto-3"]
                ? nostoPlacements["productpage-nosto-3"].html || nostoPlacements["productpage-nosto-3"]
                : ""
            }}
          ></div>
          <ProductCarouselSection builderTargetingData={builderTargetingData} />
          {/* Load PDP Impact Content */}
          <div className="pdp-impact-content" ref={impactContentRef}>
            {impactContentVisible && <BuilderComponent model="pdp-impact-content"/>}
          </div></>):(
        <Error/>
      )
    }
    </>
  );
}
